import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import dayjs from "dayjs"
import { Bounce, toast } from "react-toastify"
import DashboardCard from "../../../components/DashboardComponents/DashboardCard"
import ListingTabs from "../../../components/Listing/ListingTabs"
import SearchInput from "../../../components/Listing/SearchInput"
import FilterDropdown from "../../../components/Listing/FilterDropdown"
import ExportLogButton from "../../../components/Listing/ExportLogButton"
import Pagination from "../../../components/Listing/Pagination"
import CustomButton from "../../../components/OnboardingComponents/CustomButton"
import CustomIconButton from "../../../components/OnboardingComponents/CustomIconButton"
import EmptyState from "../../../components/Common/EmptyState"
import TruncatedText from "../../../components/Common/TruncatedText"
import DeleteConfirmation from "../../../components/Common/DeleteConfirmation"
import { ICONS } from "../../../constants/icons"
import { del, get } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { ADMIN_ARTS_GENOME_TAB_PERMISSION_MODULES } from "../../../config/adminPermissionRegistry"
import { toastApiError } from "../../../helpers/apiError"
import { filterAdminArtsGenomeTabs } from "../../../helpers/adminPermissions"
import useAdminPermission from "../../../hooks/useAdminPermission"
import {
  ARTS_GENOME_TABS,
  ARTS_GENOME_SORT_OPTIONS,
  DEFAULT_ARTS_GENOME_SORT_BY,
  DEFAULT_ARTS_GENOME_SORT_DIRECTION,
  mapArtsGenomeSummaryKpi,
  mapKeywordMappingRow,
  mapRoleClassRow,
  mapSpellbookRow,
  resolveClassPillTone,
  resolveSpellbookPillTone,
  TAB_CONFIG,
} from "./adminArtsGenomeData"

const PAGE_SIZE = 15

const KEYWORD_STATUS_LABEL = {
  active: "Active",
  inactive: "Inactive",
}

const formatDateTime = value => {
  if (!value) return "--"
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.format("MMM D, YYYY hh:mm A") : "--"
}

const MappingPill = ({ children, tone = "neutral", variant = "solid" }) => (
  <TruncatedText
    className={`admin-arts-genome__pill admin-arts-genome__pill--${variant} admin-arts-genome__pill--${tone}`}
    title={typeof children === "string" ? children : undefined}
  >
    {children}
  </TruncatedText>
)

const MappingStatus = ({ status }) => (
  <span
    className={`admin-arts-genome__status admin-arts-genome__status--${
      status === "inactive" ? "inactive" : "active"
    }`}
  >
    {KEYWORD_STATUS_LABEL[status] || status}
  </span>
)

const renderKeywordCell = (row, column) => {
  const value = row[column.key]

  if (column.type === "spellbook-pill") {
    if (!value || value === "--") {
      return (
        <TruncatedText className="admin-arts-genome__cell">--</TruncatedText>
      )
    }
    const toneSource = column.toneKey ? row[column.toneKey] : value
    return (
      <MappingPill tone={resolveSpellbookPillTone(toneSource)} variant="solid">
        {value}
      </MappingPill>
    )
  }

  if (column.type === "class-pill") {
    if (!value || value === "--") {
      return (
        <TruncatedText className="admin-arts-genome__cell">--</TruncatedText>
      )
    }
    const toneSource = column.toneKey ? row[column.toneKey] : value
    return (
      <MappingPill tone={resolveClassPillTone(toneSource)} variant="soft">
        {value}
      </MappingPill>
    )
  }

  if (column.type === "status") {
    return <MappingStatus status={value} />
  }

  if (column.key === "createdAt") {
    return (
      <TruncatedText className="admin-arts-genome__cell">
        {formatDateTime(value)}
      </TruncatedText>
    )
  }

  return (
    <TruncatedText className="admin-arts-genome__cell">
      {value || "--"}
    </TruncatedText>
  )
}

/**
 * Admin Arts Genome — keyword mappings via /api/v1/admin/role-types
 */
const AdminArtsGenome = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { can, permissionState } = useAdminPermission()
  const visibleTabs = useMemo(
    () => filterAdminArtsGenomeTabs(permissionState, ARTS_GENOME_TABS),
    [permissionState],
  )
  const initialTab = location.state?.tab
  const [activeTab, setActiveTab] = useState(
    ARTS_GENOME_TABS.some(tab => tab.key === initialTab)
      ? initialTab
      : "spellbook",
  )
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [sortBy, setSortBy] = useState(DEFAULT_ARTS_GENOME_SORT_BY)
  const [sortDirection, setSortDirection] = useState(
    DEFAULT_ARTS_GENOME_SORT_DIRECTION,
  )
  const [currentPage, setCurrentPage] = useState(1)

  const [keywordRows, setKeywordRows] = useState([])
  const [keywordTotal, setKeywordTotal] = useState(0)
  const [keywordPageSize, setKeywordPageSize] = useState(PAGE_SIZE)
  const [isKeywordLoading, setIsKeywordLoading] = useState(false)
  const [keywordError, setKeywordError] = useState(null)
  const [spellbookRows, setSpellbookRows] = useState([])
  const [spellbookTotal, setSpellbookTotal] = useState(0)
  const [spellbookPageSize, setSpellbookPageSize] = useState(PAGE_SIZE)
  const [isSpellbookLoading, setIsSpellbookLoading] = useState(false)
  const [spellbookError, setSpellbookError] = useState(null)
  const [classRows, setClassRows] = useState([])
  const [classTotal, setClassTotal] = useState(0)
  const [classPageSize, setClassPageSize] = useState(PAGE_SIZE)
  const [isClassLoading, setIsClassLoading] = useState(false)
  const [classError, setClassError] = useState(null)
  const [kpiCards, setKpiCards] = useState(() => mapArtsGenomeSummaryKpi())
  const [isKpiLoading, setIsKpiLoading] = useState(true)
  const [isExporting, setIsExporting] = useState(false)
  const [itemToDelete, setItemToDelete] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const tabConfig = TAB_CONFIG[activeTab]
  const tabModule = ADMIN_ARTS_GENOME_TAB_PERMISSION_MODULES[activeTab]
  const canCreate = Boolean(tabModule && can(tabModule, "create"))
  const canEdit = Boolean(tabModule && can(tabModule, "edit"))
  const canDelete = Boolean(tabModule && can(tabModule, "delete"))
  const isKeywordTab = activeTab === "keyword"
  const isSpellbookTab = activeTab === "spellbook"
  const isClassTab = activeTab === "class"

  useEffect(() => {
    document.title = "Arts Genome | AMOK . EARTH Admin"
  }, [])

  useEffect(() => {
    if (!visibleTabs.some(tab => tab.key === activeTab)) {
      setActiveTab(visibleTabs[0]?.key || "spellbook")
    }
  }, [activeTab, visibleTabs])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    setCurrentPage(1)
  }, [activeTab, debouncedSearch, sortBy, sortDirection])

  useEffect(() => {
    setSortBy(prev =>
      ARTS_GENOME_SORT_OPTIONS.some(option => option.value === prev)
        ? prev
        : DEFAULT_ARTS_GENOME_SORT_BY,
    )
  }, [activeTab])

  useEffect(() => {
    let cancelled = false
    const fetchSummary = async () => {
      setIsKpiLoading(true)
      try {
        const response = await get(endpoints.getAdminRoleTypesSummary)
        if (cancelled) return
        if (response?.data?.success) {
          setKpiCards(mapArtsGenomeSummaryKpi(response.data.result || {}))
        } else {
          toastApiError(response, "Failed to load Arts Genome summary")
        }
      } catch (err) {
        if (!cancelled) {
          toastApiError(err, "Failed to load Arts Genome summary")
        }
      } finally {
        if (!cancelled) setIsKpiLoading(false)
      }
    }
    fetchSummary()
    return () => {
      cancelled = true
    }
  }, [])

  const fetchKeywords = useCallback(async () => {
    setIsKeywordLoading(true)
    setKeywordError(null)
    try {
      const params = {
        page: currentPage,
        page_size: PAGE_SIZE,
        sort_by: sortBy,
        sort_direction: sortDirection,
      }
      if (debouncedSearch) params.search = debouncedSearch

      const response = await get(endpoints.getAdminRoleTypes, { params })
      if (response?.data?.success) {
        const result = response.data.result || {}
        const mapped = (result.data || []).map(mapKeywordMappingRow)

        setKeywordRows(mapped)
        setKeywordTotal(result.total || mapped.length)
        setKeywordPageSize(result.per_page || PAGE_SIZE)

        const totalPages = Math.max(
          1,
          Math.ceil(
            (result.total || mapped.length) / (result.per_page || PAGE_SIZE),
          ),
        )
        if (currentPage > totalPages) setCurrentPage(totalPages)
      } else {
        const message = toastApiError(
          response,
          "Failed to load keyword mappings",
        )
        setKeywordError(message)
        setKeywordRows([])
        setKeywordTotal(0)
      }
    } catch (err) {
      const message = toastApiError(err, "Failed to load keyword mappings")
      setKeywordError(message)
      setKeywordRows([])
      setKeywordTotal(0)
    } finally {
      setIsKeywordLoading(false)
    }
  }, [currentPage, debouncedSearch, sortBy, sortDirection])

  useEffect(() => {
    if (!isKeywordTab) return
    fetchKeywords()
  }, [fetchKeywords, isKeywordTab])

  const fetchSpellbooks = useCallback(async () => {
    setIsSpellbookLoading(true)
    setSpellbookError(null)
    try {
      const params = {
        page: currentPage,
        page_size: PAGE_SIZE,
        sort_by: sortBy,
        sort_direction: sortDirection,
      }
      if (debouncedSearch) params.search = debouncedSearch

      const response = await get(endpoints.getAdminSpellbooks, { params })
      if (response?.data?.success) {
        const result = response.data.result || {}
        const mapped = (result.data || []).map(mapSpellbookRow)

        setSpellbookRows(mapped)
        setSpellbookTotal(result.total || mapped.length)
        setSpellbookPageSize(result.per_page || PAGE_SIZE)

        const totalPages = Math.max(
          1,
          Math.ceil(
            (result.total || mapped.length) / (result.per_page || PAGE_SIZE),
          ),
        )
        if (currentPage > totalPages) setCurrentPage(totalPages)
      } else {
        const message = toastApiError(response, "Failed to load spellbooks")
        setSpellbookError(message)
        setSpellbookRows([])
        setSpellbookTotal(0)
      }
    } catch (err) {
      const message = toastApiError(err, "Failed to load spellbooks")
      setSpellbookError(message)
      setSpellbookRows([])
      setSpellbookTotal(0)
    } finally {
      setIsSpellbookLoading(false)
    }
  }, [currentPage, debouncedSearch, sortBy, sortDirection])

  useEffect(() => {
    if (!isSpellbookTab) return
    fetchSpellbooks()
  }, [fetchSpellbooks, isSpellbookTab])

  const fetchRoleClasses = useCallback(async () => {
    setIsClassLoading(true)
    setClassError(null)
    try {
      const params = {
        page: currentPage,
        page_size: PAGE_SIZE,
        sort_by: sortBy,
        sort_direction: sortDirection,
      }
      if (debouncedSearch) params.search = debouncedSearch

      const response = await get(endpoints.getAdminRoleClasses, { params })
      if (response?.data?.success) {
        const result = response.data.result || {}
        const mapped = (result.data || []).map(mapRoleClassRow)

        setClassRows(mapped)
        setClassTotal(result.total || mapped.length)
        setClassPageSize(result.per_page || PAGE_SIZE)

        const totalPages = Math.max(
          1,
          Math.ceil(
            (result.total || mapped.length) / (result.per_page || PAGE_SIZE),
          ),
        )
        if (currentPage > totalPages) setCurrentPage(totalPages)
      } else {
        const message = toastApiError(response, "Failed to load class types")
        setClassError(message)
        setClassRows([])
        setClassTotal(0)
      }
    } catch (err) {
      const message = toastApiError(err, "Failed to load class types")
      setClassError(message)
      setClassRows([])
      setClassTotal(0)
    } finally {
      setIsClassLoading(false)
    }
  }, [currentPage, debouncedSearch, sortBy, sortDirection])

  useEffect(() => {
    if (!isClassTab) return
    fetchRoleClasses()
  }, [fetchRoleClasses, isClassTab])

  const pageRows = isKeywordTab
    ? keywordRows
    : isSpellbookTab
      ? spellbookRows
      : classRows
  const totalItems = isKeywordTab
    ? keywordTotal
    : isSpellbookTab
      ? spellbookTotal
      : classTotal
  const pageSize = isKeywordTab
    ? keywordPageSize
    : isSpellbookTab
      ? spellbookPageSize
      : classPageSize
  const paginationPage = currentPage
  const isListLoading =
    (isKeywordTab && isKeywordLoading) ||
    (isSpellbookTab && isSpellbookLoading) ||
    (isClassTab && isClassLoading)
  const isNamedEntityLoading =
    (isSpellbookTab && isSpellbookLoading) || (isClassTab && isClassLoading)
  const namedEntityError = isSpellbookTab ? spellbookError : classError
  const namedEntityRetry = isSpellbookTab ? fetchSpellbooks : fetchRoleClasses

  const handleAdd = () => {
    navigate(`/admin/arts-genome/${activeTab}/create`)
  }

  const handleEdit = row => {
    let state
    if (isKeywordTab) state = { keyword: row.raw || row }
    else if (isSpellbookTab) state = { spellbook: row.raw || row }
    else if (isClassTab) state = { roleClass: row.raw || row }
    navigate(`/admin/arts-genome/${activeTab}/${row.id}/edit`, { state })
  }

  const handleDelete = row => {
    setItemToDelete({ ...row, _deleteTab: activeTab })
  }

  const closeDeleteModal = () => {
    if (isDeleting) return
    setItemToDelete(null)
  }

  const handleDeleteConfirm = async () => {
    if (!itemToDelete?.id || isDeleting) return

    const deleteTab = itemToDelete._deleteTab || "keyword"
    const deleteConfig = {
      spellbook: {
        endpoint: endpoints.deleteAdminSpellbook,
        failMessage: "Failed to delete spellbook",
        label: itemToDelete.name,
        refresh: fetchSpellbooks,
      },
      class: {
        endpoint: endpoints.deleteAdminRoleClass,
        failMessage: "Failed to delete class type",
        label: itemToDelete.name,
        refresh: fetchRoleClasses,
      },
      keyword: {
        endpoint: endpoints.deleteAdminRoleType,
        failMessage: "Failed to delete keyword mapping",
        label: itemToDelete.publicRole || itemToDelete.roleInternal,
        refresh: fetchKeywords,
      },
    }[deleteTab] || {
      endpoint: endpoints.deleteAdminRoleType,
      failMessage: "Failed to delete item",
      label: itemToDelete.name,
      refresh: fetchKeywords,
    }

    setIsDeleting(true)
    try {
      const response = await del(deleteConfig.endpoint(itemToDelete.id), {
        skipErrorToast: true,
      })

      if (
        !response ||
        response.status >= 400 ||
        response?.data?.success === false
      ) {
        toastApiError(response, deleteConfig.failMessage)
        return
      }

      toast.success(
        response?.data?.message || `“${deleteConfig.label}” deleted.`,
        {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        },
      )
      setItemToDelete(null)
      await deleteConfig.refresh()

      try {
        const summaryResponse = await get(endpoints.getAdminRoleTypesSummary)
        if (summaryResponse?.data?.success) {
          setKpiCards(
            mapArtsGenomeSummaryKpi(summaryResponse.data.result || {}),
          )
        }
      } catch {
        // list refresh already succeeded
      }
    } catch (err) {
      toastApiError(err, deleteConfig.failMessage)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleExport = async () => {
    if (isExporting) return

    const exportConfig = isSpellbookTab
      ? {
          label: "spellbooks",
          endpoint: endpoints.exportAdminSpellbooks,
          filename: `spellbooks-${dayjs().format("YYYY-MM-DD")}.csv`,
          success: "Spellbooks exported.",
        }
      : isClassTab
        ? {
            label: "class types",
            endpoint: endpoints.exportAdminRoleClasses,
            filename: `role-classes-${dayjs().format("YYYY-MM-DD")}.csv`,
            success: "Class types exported.",
          }
        : {
            label: "keyword mappings",
            endpoint: endpoints.exportAdminRoleTypes,
            filename: `role-types-${dayjs().format("YYYY-MM-DD")}.csv`,
            success: "Keyword mappings exported.",
          }

    setIsExporting(true)
    try {
      const params = {
        sort_by: sortBy,
        sort_direction: sortDirection,
      }
      if (debouncedSearch) params.search = debouncedSearch

      const response = await get(exportConfig.endpoint, {
        params,
        responseType: "blob",
        skipErrorToast: true,
      })

      if (!response || response.status >= 400) {
        toastApiError(response, `Failed to export ${exportConfig.label}`)
        return
      }

      const contentType = String(
        response.headers?.["content-type"] || "",
      ).toLowerCase()

      if (contentType.includes("application/json")) {
        const text = await response.data.text()
        let message = `Failed to export ${exportConfig.label}`
        try {
          const parsed = JSON.parse(text)
          message = parsed?.message || message
        } catch {
          // keep default
        }
        toast.error(message, {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        })
        return
      }

      const disposition = response.headers?.["content-disposition"] || ""
      const filenameMatch = disposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i,
      )
      const filename = filenameMatch?.[1]
        ? filenameMatch[1].replace(/['"]/g, "")
        : exportConfig.filename

      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: "text/csv;charset=utf-8;" })
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)

      toast.success(exportConfig.success, {
        position: "bottom-center",
        theme: "colored",
        transition: Bounce,
      })
    } catch (err) {
      toastApiError(err, `Failed to export ${exportConfig.label}`)
    } finally {
      setIsExporting(false)
    }
  }

  const skeletonRows = useMemo(
    () => Array.from({ length: 6 }, (_, index) => `skeleton-${index}`),
    [],
  )

  const sortFilters = useMemo(
    () => [
      {
        key: "sortBy",
        type: "sortBy",
        label: "Sort By",
        value: sortBy,
        defaultValue: DEFAULT_ARTS_GENOME_SORT_BY,
        onChange: next => {
          setSortBy(next || DEFAULT_ARTS_GENOME_SORT_BY)
        },
        options: ARTS_GENOME_SORT_OPTIONS,
      },
      {
        key: "sortDirection",
        type: "sortDirection",
        label: "Order",
        value: sortDirection,
        defaultValue: DEFAULT_ARTS_GENOME_SORT_DIRECTION,
        onChange: next => {
          setSortDirection(next || DEFAULT_ARTS_GENOME_SORT_DIRECTION)
        },
      },
    ],
    [sortBy, sortDirection],
  )

  const deleteTab = itemToDelete?._deleteTab
  const deleteModalCopy = {
    spellbook: {
      title: "Delete Spellbook",
      confirmText: "Delete Spellbook",
      message: itemToDelete
        ? `Are you sure you want to delete “${itemToDelete.name}”? This action cannot be undone.`
        : "",
    },
    class: {
      title: "Delete Class Type",
      confirmText: "Delete Class Type",
      message: itemToDelete
        ? `Are you sure you want to delete “${itemToDelete.name}”? This action cannot be undone.`
        : "",
    },
    keyword: {
      title: "Delete Keyword Mapping",
      confirmText: "Delete Mapping",
      message: itemToDelete
        ? `Are you sure you want to delete “${
            itemToDelete.publicRole || itemToDelete.roleInternal
          }”? This action cannot be undone.`
        : "",
    },
  }[deleteTab] || {
    title: "Delete Item",
    confirmText: "Delete",
    message: "",
  }

  return (
    <div className="admin-page admin-arts-genome">
      <div className="admin-arts-genome__kpi-grid">
        {kpiCards
          .filter(card =>
            can(ADMIN_ARTS_GENOME_TAB_PERMISSION_MODULES[card.key], "view"),
          )
          .map(card => (
            <DashboardCard
              key={card.key}
              title={card.title}
              tone={card.tone}
              className="admin-arts-genome__kpi-card"
              fullHeight
            >
              <p className="admin-arts-genome__kpi-value">
                {isKpiLoading ? (
                  <span className="admin-arts-genome__skeleton admin-arts-genome__skeleton--kpi" />
                ) : (
                  card.value
                )}
              </p>
            </DashboardCard>
          ))}
      </div>

      <div className="admin-arts-genome__toolbar">
        <div className="admin-arts-genome__toolbar-left">
          <ListingTabs
            items={visibleTabs}
            activeKey={activeTab}
            onChange={setActiveTab}
          />
        </div>
        <div className="admin-arts-genome__controls">
          <SearchInput
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder={tabConfig.searchPlaceholder}
            iconSrc={ICONS.callFormalSearchStatus}
            className="admin-arts-genome__search"
          />
          <FilterDropdown label="Filter" filters={sortFilters} />
          <ExportLogButton
            entityLabel="arts genome"
            onClick={handleExport}
            loading={isExporting}
            disabled={isExporting}
          />
          {canCreate ? (
            <CustomButton
              variant="primary"
              size="md"
              type="button"
              trailingIconSrc={ICONS.plus}
              trailingIconAlt=""
              onClick={handleAdd}
            >
              {tabConfig.addLabel}
            </CustomButton>
          ) : null}
        </div>
      </div>

      <div className="admin-arts-genome__table-wrapper">
        <div
          className={`admin-arts-genome__table${
            isKeywordTab ? " admin-arts-genome__table--keyword" : ""
          }`}
          role="table"
          aria-busy={isListLoading || undefined}
        >
          {isKeywordTab ? (
            <>
              <div
                className="admin-arts-genome__table-row admin-arts-genome__table-row--head"
                role="row"
              >
                {tabConfig.columns.map(column => (
                  <div
                    key={column.key}
                    role="columnheader"
                    className={
                      column.key === "status"
                        ? "admin-arts-genome__status-head"
                        : undefined
                    }
                  >
                    {column.label}
                  </div>
                ))}
                <div
                  role="columnheader"
                  className="admin-arts-genome__actions-head"
                >
                  Actions
                </div>
              </div>

              {isKeywordLoading &&
                skeletonRows.map(key => (
                  <div
                    key={key}
                    className="admin-arts-genome__table-row admin-arts-genome__table-row--skeleton"
                    role="row"
                  >
                    {tabConfig.columns.map(column => (
                      <div
                        key={column.key}
                        className="admin-arts-genome__skeleton"
                      />
                    ))}
                    <div className="admin-arts-genome__skeleton" />
                  </div>
                ))}

              {!isKeywordLoading &&
                pageRows.map(row => (
                  <div
                    key={row.id}
                    className="admin-arts-genome__table-row"
                    role="row"
                  >
                    {tabConfig.columns.map(column => (
                      <div key={column.key} role="cell">
                        {renderKeywordCell(row, column)}
                      </div>
                    ))}
                    <div className="admin-arts-genome__actions" role="cell">
                      {canEdit ? (
                        <CustomIconButton
                          iconSrc={ICONS.edit}
                          label={`Edit ${row.publicRole}`}
                          size="sm"
                          onClick={() => handleEdit(row)}
                        />
                      ) : null}
                      {canDelete ? (
                        <CustomIconButton
                          iconSrc={ICONS.trash}
                          label={`Delete ${row.publicRole}`}
                          tone="danger"
                          size="sm"
                          onClick={() => handleDelete(row)}
                        />
                      ) : null}
                    </div>
                  </div>
                ))}

              {!isKeywordLoading && !keywordError && pageRows.length === 0 && (
                <div className="admin-arts-genome__empty">
                  <EmptyState
                    title="No items found"
                    description="Try adjusting your search or filters."
                  />
                </div>
              )}

              {!isKeywordLoading && keywordError && pageRows.length === 0 && (
                <div className="admin-arts-genome__empty">
                  <EmptyState
                    title="Unable to load keyword mappings"
                    description={keywordError}
                    actionText="Try again"
                    onActionClick={fetchKeywords}
                  />
                </div>
              )}
            </>
          ) : (
            <>
              <div
                className="admin-arts-genome__table-row admin-arts-genome__table-row--head"
                role="row"
              >
                <div role="columnheader">{tabConfig.nameHeader}</div>
                <div role="columnheader">Created At</div>
                <div role="columnheader">Uploaded At</div>
                <div
                  role="columnheader"
                  className="admin-arts-genome__actions-head"
                >
                  Action
                </div>
              </div>

              {isNamedEntityLoading &&
                skeletonRows.map(key => (
                  <div
                    key={key}
                    className="admin-arts-genome__table-row admin-arts-genome__table-row--skeleton"
                    role="row"
                  >
                    <div className="admin-arts-genome__skeleton" />
                    <div className="admin-arts-genome__skeleton" />
                    <div className="admin-arts-genome__skeleton" />
                    <div className="admin-arts-genome__skeleton" />
                  </div>
                ))}

              {!isNamedEntityLoading &&
                pageRows.map(row => (
                  <div
                    key={row.id}
                    className="admin-arts-genome__table-row"
                    role="row"
                  >
                    <div className="admin-arts-genome__name-cell" role="cell">
                      <div
                        className="admin-arts-genome__avatar"
                        aria-hidden="true"
                      >
                        {row.initials}
                      </div>
                      <TruncatedText className="admin-arts-genome__name">
                        {row.name}
                      </TruncatedText>
                    </div>
                    <TruncatedText
                      as="div"
                      className="admin-arts-genome__cell"
                      role="cell"
                    >
                      {formatDateTime(row.createdAt)}
                    </TruncatedText>
                    <TruncatedText
                      as="div"
                      className="admin-arts-genome__cell"
                      role="cell"
                    >
                      {formatDateTime(row.uploadedAt)}
                    </TruncatedText>
                    <div className="admin-arts-genome__actions" role="cell">
                      {canEdit ? (
                        <CustomIconButton
                          iconSrc={ICONS.edit}
                          label={`Edit ${row.name}`}
                          size="sm"
                          onClick={() => handleEdit(row)}
                        />
                      ) : null}
                      {canDelete ? (
                        <CustomIconButton
                          iconSrc={ICONS.trash}
                          label={`Delete ${row.name}`}
                          tone="danger"
                          size="sm"
                          onClick={() => handleDelete(row)}
                        />
                      ) : null}
                    </div>
                  </div>
                ))}

              {!isNamedEntityLoading &&
                !namedEntityError &&
                pageRows.length === 0 && (
                  <div className="admin-arts-genome__empty">
                    <EmptyState
                      title="No items found"
                      description="Try adjusting your search or filters."
                    />
                  </div>
                )}

              {!isNamedEntityLoading &&
                namedEntityError &&
                pageRows.length === 0 && (
                  <div className="admin-arts-genome__empty">
                    <EmptyState
                      title={
                        isSpellbookTab
                          ? "Unable to load spellbooks"
                          : "Unable to load class types"
                      }
                      description={namedEntityError}
                      actionText="Try again"
                      onActionClick={namedEntityRetry}
                    />
                  </div>
                )}
            </>
          )}
        </div>
      </div>

      <Pagination
        currentPage={paginationPage}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        itemLabel={tabConfig.paginationLabel}
        prevIconSrc={ICONS.chevronLeft}
        nextIconSrc={ICONS.chevronRight}
        className="admin-arts-genome__pagination"
      />

      <DeleteConfirmation
        isOpen={Boolean(itemToDelete)}
        onCancel={closeDeleteModal}
        onConfirm={handleDeleteConfirm}
        title={deleteModalCopy.title}
        message={deleteModalCopy.message}
        cancelText="Cancel"
        confirmText={deleteModalCopy.confirmText}
        confirmLoadingText="Deleting..."
        isDeleting={isDeleting}
      />
    </div>
  )
}

export default AdminArtsGenome
